// [claude-code 2026-05-05] Responsive shell compaction: icon-only header dropdown triggers, context-aware panel-toggle visibility by layout mode, and nametag suppression at marginal widths.
// [claude-code 2026-05-01] v6.0.5: removed toolbar's own border-t so it doesn't double-stroke the new MainContent top border
// [claude-code 2026-03-03] Toolbar items reorderable via getToolbarOrder/setToolbarOrder.
// [claude-code 2026-03-11] T2: IV score wired to backend /api/market-data/iv-score — replaces local quickIVScore
// [claude-code 2026-03-20] S3:T4b: Merge platform/layout into one toolbar slot; DND moves to header when iFrame active
// [claude-code 2026-03-20] S3:T4c: createPortal for platform/layout dropdowns — fixes z-index behind Strategium panel
// [claude-code 2026-03-20] S3:T5 — VIX spike toast trigger when VIX crosses above threshold
// [claude-code 2026-03-24] Change VIX risk toast from threshold-crossing to scheduled pre-market-open times (EST)
// [claude-code 2026-05-16] S67: lockout dropdown with briefing anchor options, toolbar icon colors → var(--fintheon-accent), pill bg removed, d+h timer format
import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { useAuth } from "../../contexts/AuthContext";
import { IVScoreCard } from "../IVScoreCard";
import { useBackend } from "../../lib/backend";
import { useSettings } from "../../contexts/SettingsContext";
import { useToast } from "../../contexts/ToastContext";
import {
  getToolbarOrder,
  type ToolbarItemId,
} from "../../lib/layoutOrderStorage";
import { HeaderVoiceControl } from "../voice/HeaderVoiceControl";
import { PanelToggleButton } from "./PanelToggleGroup";
import {
  Layers,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Menu,
  Monitor,
  MessageCircle,
  Power,
  Bell,
  BellOff,
  ClipboardList,
  Clock705,
  CreditCard,
  Download,
  LogOut,
  Lock,
  TrendingUp,
  User,
  type LucideIcon,
} from "lucide-react";
import { StickyBulletin } from "../StickyBulletin";
import { TraderNametag } from "../TraderNametag";
import { FadingRuler } from "../shared/FadingRuler";
import { TiltScoutHeaderFuse } from "../narrative/TiltScoutHeaderFuse";
import { ProxVoiceHeaderControl } from "../proxvoice/ProxVoiceHeaderControl";
import type { IVScoreResponse } from "../../types/market-data";
import type { EconHeaderCountdownEvent } from "../../types/econ-header";
import type { TradingPlatform } from "../TradingBrowser";
import { useDND } from "../../contexts/DNDContext";
import { useServerNotifications } from "../../contexts/NotificationsContext";
import { getNotificationDotTone } from "../../lib/notification-presentation";
import { ToolbarDnD } from "./ToolbarDnD";
import { HeaderLockButton } from "./HeaderLockButton";
import { HeaderVixChip } from "./HeaderVixChip";
import type { SurfaceCapabilities } from "../../lib/surface-capabilities";
import type { SectionId } from "../../hooks/useStickyBulletin";
import { activeInstrumentFromSettings } from "../../lib/instruments";
import { publishIvScore } from "../../lib/iv-score-store";
import { useProxVoice } from "../../contexts/ProxVoiceContext";
import {
  PUBLIC_PROFILE_UPDATED_EVENT,
  publicProfileDraftFromProxVoice,
  readPublicProfileDraft,
  writePublicProfileDraft,
  type PublicProfileDraft,
} from "../../lib/public-profile-store";
import {
  readUserAvatarUrl,
  readUserDisplayName,
} from "../../lib/user-profile-metadata";
import { WEEKLY_WALKTHROUGH_EVENTS } from "../../lib/weekly-walkthroughs";
import {
  fetchLatestDesktopDownload,
  type DesktopDownload,
} from "../../lib/desktop-download";

type NavTab =
  | "feed"
  | "analysis"
  | "riskflow"
  | "dashboard"
  | "econ"
  | "narrative"
  | "performance"
  | "proposals"
  | "desk-ops"
  | "apparatus"
  | "scouts"
  | "settings";

const TAB_LABELS: Record<NavTab, string> = {
  dashboard: "Desk",
  feed: "Desk", // feed section removed; fallback for history
  analysis: "Consilium",
  proposals: "Desk",
  apparatus: "Apparatus",
  scouts: "Scouts",
  riskflow: "RiskFlow",
  econ: "Econ Calendar",
  narrative: "DeskMap",
  performance: "Performance",
  "desk-ops": "Desk Ops",
  settings: "Settings",
};

type LayoutOption = "tickers-only" | "combined";
type QuickSettingsTarget = "profile" | "billing" | "trading" | "lockout";
type QuickSettingsAction = QuickSettingsTarget | "sign-out" | "download";

const TOOLBAR_PILL_CLASS = "flex items-center gap-0.5 h-8 rounded-md px-1";

const QUICK_SETTINGS_ITEMS: Array<{
  id: QuickSettingsAction;
  label: string;
  description: string;
  icon: LucideIcon;
}> = [
  {
    id: "profile",
    label: "Profile",
    description: "Identity and account basics",
    icon: User,
  },
  {
    id: "billing",
    label: "Billing",
    description: "Plan and access",
    icon: CreditCard,
  },
  {
    id: "trading",
    label: "Trading",
    description: "Desk and risk defaults",
    icon: TrendingUp,
  },
  {
    id: "lockout",
    label: "Lockout",
    description: "Focus guardrails",
    icon: Lock,
  },
  {
    id: "download",
    label: "Download",
    description: "Fintheon Desktop for macOS",
    icon: Download,
  },
  {
    id: "sign-out",
    label: "Sign out",
    description: "End this session",
    icon: LogOut,
  },
];

function activeIconStyle(color: string) {
  return { "--toolbar-icon-active-color": color } as React.CSSProperties;
}

interface TopHeaderProps {
  topStepXEnabled?: boolean;
  onTopStepXToggle?: () => void;
  onTopStepXDisable?: () => void;
  selectedPlatform?: TradingPlatform;
  onPlatformSelect?: (platform: TradingPlatform) => void;
  layoutOption?: LayoutOption;
  onLayoutOptionChange?: (option: LayoutOption) => void;
  chatOpen?: boolean;
  onChatToggle?: () => void;
  activeTab?: NavTab;
  tabHistory?: NavTab[];
  historyIndex?: number;
  onBack?: () => void;
  onForward?: () => void;
  hideBranding?: boolean;
  psychAssistHeadingWidget?: React.ReactNode;
  voiceRoomWidget?: React.ReactNode;
  performanceChatWidget?: React.ReactNode;
  econCountdownWidget?: React.ReactNode;
  latestReleasedEconEvent?: EconHeaderCountdownEvent | null;
  compactLevel?: 0 | 1 | 2;
  allowCustomIframes?: boolean;
  surfaceCapabilities?: SurfaceCapabilities;
  mobileDrawerOpen?: boolean;
  onMobileDrawerToggle?: () => void;
}

export function TopHeader({
  topStepXEnabled = false,
  onTopStepXToggle,
  onTopStepXDisable,
  selectedPlatform = "topstepx",
  onPlatformSelect,
  layoutOption = "combined",
  onLayoutOptionChange,
  chatOpen = false,
  onChatToggle,
  activeTab = "dashboard",
  tabHistory = [],
  historyIndex = 0,
  onBack,
  onForward,
  psychAssistHeadingWidget,
  voiceRoomWidget,
  performanceChatWidget,
  econCountdownWidget,
  latestReleasedEconEvent,
  compactLevel = 0,
  allowCustomIframes = true,
  surfaceCapabilities,
  mobileDrawerOpen = false,
  onMobileDrawerToggle,
}: TopHeaderProps) {
  const { tier, signOut, user } = useAuth();
  const backend = useBackend();
  const {
    selectedSymbol,
    selectedInstrument,
    traderName,
    alertConfig,
    proposerIframeSources,
  } = useSettings();
  const activeInstrument = activeInstrumentFromSettings({
    selectedInstrument,
    selectedSymbol,
  });
  const displayedTraderName =
    traderName.trim() ||
    readUserDisplayName(user?.user_metadata) ||
    user?.email?.split("@")[0] ||
    "Trader";
  const [publicProfile, setPublicProfile] = useState<PublicProfileDraft>(() =>
    readPublicProfileDraft(),
  );
  const traderAvatarUrl =
    publicProfile.avatarUrl || readUserAvatarUrl(user?.user_metadata);
  const { addToast } = useToast();
  const [ivData, setIvData] = useState<IVScoreResponse | null>(null);
  const [ivLoading, setIvLoading] = useState(true);
  const [showLayoutDropdown, setShowLayoutDropdown] = useState(false);
  const [showPlatformDropdown, setShowPlatformDropdown] = useState(false);
  const [showQuickSettings, setShowQuickSettings] = useState(false);
  const [toolbarOrder, setToolbarOrderState] = useState<ToolbarItemId[]>(() =>
    getToolbarOrder(),
  );
  const [showBulletin, setShowBulletin] = useState(false);
  const [bulletinInitialSection, setBulletinInitialSection] = useState<
    SectionId | undefined
  >();
  const bulletinBtnRef = useRef<HTMLButtonElement>(null);
  const identityChipRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const platformDropdownRef = useRef<HTMLDivElement>(null);
  const quickSettingsPortalRef = useRef<HTMLDivElement>(null);
  const forumVoice = useProxVoice();

  useEffect(() => {
    const onProfileUpdated = (event: Event) => {
      setPublicProfile(
        (event as CustomEvent<PublicProfileDraft>).detail ??
          readPublicProfileDraft(),
      );
    };
    window.addEventListener(PUBLIC_PROFILE_UPDATED_EVENT, onProfileUpdated);
    return () =>
      window.removeEventListener(
        PUBLIC_PROFILE_UPDATED_EVENT,
        onProfileUpdated,
      );
  }, []);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    backend.proxVoice
      .getProfile()
      .then((response) => {
        if (cancelled) return;
        const nextProfile = publicProfileDraftFromProxVoice(response.profile);
        setPublicProfile(nextProfile);
        writePublicProfileDraft(nextProfile);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [backend.proxVoice, user]);
  const layoutPortalRef = useRef<HTMLDivElement>(null);
  const platformPortalRef = useRef<HTMLDivElement>(null);
  const [layoutDropdownPos, setLayoutDropdownPos] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const [platformDropdownPos, setPlatformDropdownPos] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const [quickSettingsPos, setQuickSettingsPos] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const [desktopDownload, setDesktopDownload] =
    useState<DesktopDownload | null>(null);
  const [desktopDownloadLoading, setDesktopDownloadLoading] = useState(false);
  const { dndActive, toggleManualDnd, queueCount, queue } = useDND();
  // [claude-code 2026-04-25] S35-Unified: badge counts server-side notifications + local queue.
  const { unreadCount: serverUnread, notifications: serverNotifications } =
    useServerNotifications();
  // [claude-code 2026-04-29] S53-T3: Econ watch health moved to FooterToolbar (S55)
  const totalBadgeCount = queueCount + serverUnread;
  const bulletinDotTone = getNotificationDotTone(serverNotifications, queue);
  const [quickClockPulse, setQuickClockPulse] = useState(false);
  const showIframeControls = allowCustomIframes;
  const isMobileSurface =
    surfaceCapabilities?.navigationMode === "underlay-drawer";
  const showDesktopDownload =
    surfaceCapabilities?.runtime === "web-pwa" && !isMobileSurface;
  const showMobileBack = isMobileSurface && historyIndex > 0;
  const allowVoiceAssistant = surfaceCapabilities?.allowVoiceAssistant ?? true;
  const showForumVoiceInPrimaryPill = !isMobileSurface && compactLevel < 1;
  const showForumVoiceHandoff =
    (isMobileSurface || compactLevel >= 1) &&
    (forumVoice.state === "connected" || forumVoice.state === "connecting");
  const shouldShowLeftPanelToggle =
    !isMobileSurface && !topStepXEnabled && compactLevel < 2;
  const shouldShowRightPanelToggle =
    !(topStepXEnabled && layoutOption === "tickers-only") && !isMobileSurface;
  const handleQuickClock = useCallback(async () => {
    const now = new Date();
    const time = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
    const dayOfWeek = now.getDay();
    setQuickClockPulse(true);
    setTimeout(() => setQuickClockPulse(false), 600);
    try {
      const apiBase = (
        import.meta.env.VITE_API_URL || "http://localhost:8080"
      ).replace(/\/$/, "");
      await fetch(`${apiBase}/api/sticky-bulletin/volwatch`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          time,
          dayOfWeek,
          instrument: activeInstrument,
          notes: "",
        }),
      });
    } catch {}
  }, [activeInstrument]);
  useEffect(() => {
    setToolbarOrderState(getToolbarOrder());
  }, []);

  useEffect(() => {
    const handleOpenBulletinSection = (event: Event) => {
      const section = (event as CustomEvent<{ section?: SectionId }>).detail
        ?.section;
      setBulletinInitialSection(section ?? "inbox");
      setShowBulletin(true);
    };
    const handleCloseBulletin = () => setShowBulletin(false);
    window.addEventListener(
      "fintheon:open-bulletin-section",
      handleOpenBulletinSection,
    );
    window.addEventListener("fintheon:close-bulletin", handleCloseBulletin);
    return () => {
      window.removeEventListener(
        "fintheon:open-bulletin-section",
        handleOpenBulletinSection,
      );
      window.removeEventListener(
        "fintheon:close-bulletin",
        handleCloseBulletin,
      );
    };
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (showLayoutDropdown) {
        const inTrigger = dropdownRef.current?.contains(target);
        const inPortal = layoutPortalRef.current?.contains(target);
        if (!inTrigger && !inPortal) setShowLayoutDropdown(false);
      }
      if (showPlatformDropdown) {
        const inTrigger = platformDropdownRef.current?.contains(target);
        const inPortal = platformPortalRef.current?.contains(target);
        if (!inTrigger && !inPortal) setShowPlatformDropdown(false);
      }
      if (showQuickSettings) {
        const inTrigger = identityChipRef.current?.contains(target);
        const inPortal = quickSettingsPortalRef.current?.contains(target);
        if (!inTrigger && !inPortal) setShowQuickSettings(false);
      }
    };

    if (showLayoutDropdown || showPlatformDropdown || showQuickSettings) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showLayoutDropdown, showPlatformDropdown, showQuickSettings]);

  // Calculate fixed position for portaled dropdowns
  useEffect(() => {
    if (!showLayoutDropdown || !dropdownRef.current) {
      setLayoutDropdownPos(null);
      return;
    }
    const rect = dropdownRef.current.getBoundingClientRect();
    const dropdownW = 288; // w-72 = 18rem
    let left = rect.right - dropdownW;
    if (left < 16) left = 16;
    setLayoutDropdownPos({ top: rect.bottom + 8, left });
  }, [showLayoutDropdown]);

  useEffect(() => {
    if (!showPlatformDropdown || !platformDropdownRef.current) {
      setPlatformDropdownPos(null);
      return;
    }
    const rect = platformDropdownRef.current.getBoundingClientRect();
    const dropdownW = 288;
    let left = rect.right - dropdownW;
    if (left < 16) left = 16;
    setPlatformDropdownPos({ top: rect.bottom + 8, left });
  }, [showPlatformDropdown]);

  useEffect(() => {
    if (!showQuickSettings || !identityChipRef.current) {
      setQuickSettingsPos(null);
      return;
    }
    const rect = identityChipRef.current.getBoundingClientRect();
    const dropdownW = 260;
    const viewportW = window.innerWidth || 0;
    let left = rect.left;
    if (left + dropdownW > viewportW - 16) left = viewportW - dropdownW - 16;
    if (left < 16) left = 16;
    setQuickSettingsPos({ top: rect.bottom + 8, left });
  }, [showQuickSettings]);

  useEffect(() => {
    if (!showQuickSettings || !showDesktopDownload || desktopDownload) return;
    let active = true;
    setDesktopDownloadLoading(true);
    void fetchLatestDesktopDownload()
      .then((download) => {
        if (active) setDesktopDownload(download);
      })
      .catch(() => {})
      .finally(() => {
        if (active) setDesktopDownloadLoading(false);
      });
    return () => {
      active = false;
    };
  }, [desktopDownload, showDesktopDownload, showQuickSettings]);

  // [claude-code 2026-04-24] Header dropdown is fully driven by the user-managed
  // iFrame catalogue (Settings → iFrames). No hardcoded list — adding/removing
  // entries there is what changes what shows up here.
  const platformOptions: Array<{
    value: TradingPlatform;
    label: string;
    description: string;
  }> = proposerIframeSources.map((s) => ({
    value: s.id as TradingPlatform,
    label: s.label,
    description: s.url,
  }));

  const selectedPlatformLabel =
    platformOptions.find((opt) => opt.value === selectedPlatform)?.label ??
    platformOptions[0]?.label ??
    "—";

  const layoutOptions: Array<{
    value: LayoutOption;
    label: string;
    description: string;
    icon: React.ReactNode;
  }> = [
    {
      value: "combined",
      label: "Castra",
      description: "Mission Control and RiskFlow stacked on the right",
      icon: <Layers className="w-4 h-4" />,
    },
    {
      value: "tickers-only",
      label: "Zen",
      description: "Supports split-frame browser view",
      icon: <Monitor className="w-4 h-4" />,
    },
  ];

  // Fetch blended IV score from backend — SSE first, 60s polling fallback
  useEffect(() => {
    let es: EventSource | null = null;
    let fallbackInterval: ReturnType<typeof setInterval> | null = null;
    publishIvScore(activeInstrument, {
      data: null,
      error: null,
      loading: true,
    });
    const apiBase = (
      import.meta.env.VITE_API_URL || "http://localhost:8080"
    ).replace(/\/$/, "");

    const fetchIVScore = async () => {
      try {
        const data = await backend.marketData.getIVScore(activeInstrument);
        setIvData(data);
        publishIvScore(activeInstrument, {
          data,
          error: null,
          loading: false,
        });
      } catch (error) {
        console.warn("[IV] Failed to fetch IV score:", error);
        publishIvScore(activeInstrument, {
          data: null,
          error:
            error instanceof Error ? error.message : "IV score unavailable",
          loading: false,
        });
      } finally {
        setIvLoading(false);
      }
    };

    const startFallbackPolling = () => {
      if (fallbackInterval) return;
      fallbackInterval = setInterval(fetchIVScore, 60_000);
    };

    fetchIVScore();

    try {
      es = new EventSource(
        `${apiBase}/api/market-data/iv-score/stream?symbol=${encodeURIComponent(activeInstrument)}`,
      );

      es.addEventListener("iv-score", (event) => {
        try {
          const data = JSON.parse(
            (event as MessageEvent).data,
          ) as IVScoreResponse;
          setIvData(data);
          setIvLoading(false);
          publishIvScore(activeInstrument, {
            data,
            error: null,
            loading: false,
          });
        } catch {
          // Ignore malformed stream payloads
        }
      });

      es.onerror = () => {
        es?.close();
        es = null;
        startFallbackPolling();
      };
    } catch {
      startFallbackPolling();
    }

    return () => {
      es?.close();
      if (fallbackInterval) clearInterval(fallbackInterval);
    };
  }, [activeInstrument, backend]);

  // VIX risk toast — fires ~5-10 min before each market session open (EST), only when VIX is elevated
  // Sessions: 9:30 AM, 10:00 AM(?), 11:30 AM(?), 12:25 PM(?), 6:00 PM Sun futures open
  const firedWindowsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!ivData) return;
    const threshold = alertConfig.vixSpikeThreshold ?? 22;
    const vixLevel = ivData.vix.level;

    const checkSchedule = () => {
      if (vixLevel < threshold) return;

      const now = new Date();
      // Convert to EST (UTC-5) / EDT (UTC-4)
      const estStr = now.toLocaleString("en-US", {
        timeZone: "America/New_York",
      });
      const est = new Date(estStr);
      const h = est.getHours();
      const m = est.getMinutes();
      const day = est.getDay(); // 0=Sun

      // Pre-market-open alert windows (EST): 9:20, 9:50, 11:20, 12:15, 5:50 (Sun only)
      const windows: Array<{
        h: number;
        m: number;
        sunOnly?: boolean;
        label: string;
      }> = [
        { h: 9, m: 20, label: "9:30 AM open" },
        { h: 9, m: 50, label: "10:00 AM session" },
        { h: 11, m: 20, label: "11:30 AM session" },
        { h: 12, m: 15, label: "12:25 PM session" },
        { h: 17, m: 50, sunOnly: true, label: "6:00 PM futures open" },
      ];

      const dateKey = `${est.getFullYear()}-${est.getMonth()}-${est.getDate()}`;

      for (const w of windows) {
        if (w.sunOnly && day !== 0) continue;
        // Fire within a 3-minute window (e.g. 9:20-9:22)
        if (h === w.h && m >= w.m && m <= w.m + 2) {
          const key = `${dateKey}-${w.h}:${w.m}`;
          if (!firedWindowsRef.current.has(key)) {
            firedWindowsRef.current.add(key);
            addToast(
              `VIX at ${vixLevel.toFixed(1)} — consider reducing risk`,
              "vix",
              `Elevated ahead of ${w.label}`,
              "vix-spike",
              "bottom-left",
            );
            break; // one toast per check
          }
        }
      }

      // Clean stale keys from previous days
      for (const key of firedWindowsRef.current) {
        if (!key.startsWith(dateKey)) firedWindowsRef.current.delete(key);
      }
    };

    checkSchedule();
    const interval = setInterval(checkSchedule, 30_000); // check every 30s
    return () => clearInterval(interval);
  }, [ivData, alertConfig.vixSpikeThreshold, addToast]);

  const getTierDisplayName = () => {
    switch (tier) {
      case "free":
        return "Pleb";
      case "fintheon":
        return "Equestrian";
      case "fintheon_plus":
        return "Equestrian+";
      case "fintheon_pro":
        return "Consul";
      default:
        return "Pleb";
    }
  };

  const openSettingsTab = useCallback((tab: QuickSettingsTarget) => {
    setShowQuickSettings(false);
    window.dispatchEvent(
      new CustomEvent("fintheon:navigate-tab", {
        detail: { tab: "settings" },
      }),
    );
    window.setTimeout(() => {
      window.dispatchEvent(
        new CustomEvent(WEEKLY_WALKTHROUGH_EVENTS.settingsTab, {
          detail: { tab },
        }),
      );
    }, 80);
  }, []);

  const handleQuickSettingsAction = useCallback(
    (action: QuickSettingsAction) => {
      if (action === "download") return;
      if (action === "sign-out") {
        setShowQuickSettings(false);
        void signOut();
        return;
      }
      openSettingsTab(action);
    },
    [openSettingsTab, signOut],
  );

  const headerJustifyClass = isMobileSurface
    ? "justify-center px-3"
    : "justify-between px-1.5";
  const leftClusterClass = isMobileSurface
    ? "absolute left-3 top-1/2 z-20 -translate-y-1/2"
    : "";
  const rightClusterClass = isMobileSurface
    ? "pointer-events-none absolute inset-0 z-10"
    : "";
  const rightInnerClass = isMobileSurface
    ? "contents"
    : "flex items-center gap-2 flex-shrink-0";
  const mobileTickerWrapperClass = isMobileSurface
    ? "pointer-events-auto absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center justify-center gap-1.5"
    : "";

  return (
    <div
      id="fintheon-heading-toolbar"
      data-tour-target="toolbar"
      data-compact-level={compactLevel}
      className={`fintheon-power-frame-surface relative z-[120] flex items-center ${headerJustifyClass} ${topStepXEnabled && layoutOption === "tickers-only" ? "h-[47px]" : "h-[50px]"}`}
    >
      <div
        className={`flex items-center gap-2 lg:gap-4 xl:gap-6 ${leftClusterClass}`}
      >
        <div className="flex items-center gap-3">
          {isMobileSurface && (
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={showMobileBack ? onBack : onMobileDrawerToggle}
                aria-label={
                  showMobileBack
                    ? "Back"
                    : mobileDrawerOpen
                      ? "Close mobile menu"
                      : "Open mobile menu"
                }
                aria-expanded={showMobileBack ? undefined : mobileDrawerOpen}
                className={`mobile-menu-glass-button fintheon-black-liquid-glass fintheon-glass-control solvys-liquid-glass relative grid h-[38px] w-[38px] place-items-center overflow-hidden rounded-full border transition-[border-color,background,color,transform] active:scale-95 ${
                  mobileDrawerOpen && !showMobileBack
                    ? "mobile-menu-glass-button--open"
                    : ""
                }`}
                title={
                  showMobileBack
                    ? "Back"
                    : mobileDrawerOpen
                      ? "Close menu"
                      : "Open menu"
                }
              >
                <ChevronLeft
                  className={`absolute h-4 w-4 transition-[opacity,transform,filter] duration-200 ${
                    showMobileBack
                      ? "translate-x-0 scale-100 opacity-100 blur-0"
                      : "-translate-x-2 scale-90 opacity-0 blur-[1px]"
                  }`}
                />
                <Menu
                  className={`absolute h-4 w-4 transition-[opacity,transform,filter] duration-200 ${
                    showMobileBack
                      ? "translate-x-2 scale-90 opacity-0 blur-[1px]"
                      : "translate-x-0 scale-100 opacity-100 blur-0"
                  }`}
                />
              </button>
            </div>
          )}
          {shouldShowLeftPanelToggle && (
            <PanelToggleButton side="left" label="left panel" />
          )}

          {/* Breadcrumb navigation — back/forward + section name */}
          {!isMobileSurface && !topStepXEnabled && compactLevel < 2 && (
            <div className="flex items-center gap-1">
              <button
                onClick={onBack}
                disabled={historyIndex <= 0}
                className="p-1 rounded text-gray-500 hover:text-[var(--fintheon-accent)] disabled:text-gray-700 disabled:cursor-default transition-colors"
                title="Back"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={onForward}
                disabled={historyIndex >= tabHistory.length - 1}
                className="p-1 rounded text-gray-500 hover:text-[var(--fintheon-accent)] disabled:text-gray-700 disabled:cursor-default transition-colors"
                title="Forward"
              >
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
              {compactLevel < 1 && (
                <span className="text-[10px] tracking-[0.18em] uppercase text-gray-300 ml-2">
                  {TAB_LABELS[activeTab] || activeTab}
                </span>
              )}
            </div>
          )}

          {compactLevel < 1 && (
            <div
              ref={identityChipRef}
              className="fintheon-identity-chip fintheon-black-liquid-glass fintheon-glass-pill header-widget-glass solvys-liquid-glass hidden xl:flex items-center h-7 overflow-hidden rounded-[6px]"
            >
              <button
                type="button"
                onClick={() => openSettingsTab("profile")}
                className="fintheon-identity-chip__name h-full"
                aria-label={`Open profile settings for ${displayedTraderName}`}
                title="Open profile settings"
              >
                {traderAvatarUrl ? (
                  <span className="fintheon-identity-chip__avatar">
                    <img src={traderAvatarUrl} alt="" />
                  </span>
                ) : null}
                <TraderNametag
                  name={displayedTraderName}
                  variant="embedded"
                  disablePulse={!(alertConfig.nametagEmoPulse ?? true)}
                />
              </button>
              <span
                aria-hidden="true"
                className="fintheon-identity-chip__split"
              />
              <button
                type="button"
                onClick={() => setShowQuickSettings((open) => !open)}
                className="fintheon-identity-chip__tier h-full px-2.5 text-[11px]"
                aria-haspopup="menu"
                aria-expanded={showQuickSettings}
                aria-label={`Open quick access settings for ${getTierDisplayName()}`}
                title="Quick access settings"
              >
                <span className="whitespace-nowrap">
                  {getTierDisplayName()}
                </span>
              </button>
              {showQuickSettings &&
                quickSettingsPos &&
                createPortal(
                  <div
                    ref={quickSettingsPortalRef}
                    style={{
                      position: "fixed",
                      top: quickSettingsPos.top,
                      left: quickSettingsPos.left,
                      zIndex: 9999,
                    }}
                    className="fintheon-quick-settings-popover w-[260px] overflow-hidden p-1.5 animate-dropdown-enter"
                    role="menu"
                    aria-label="Quick access settings"
                  >
                    {QUICK_SETTINGS_ITEMS.filter(
                      (item) => item.id !== "download" || showDesktopDownload,
                    ).map((item) => {
                      const Icon = item.icon;
                      if (item.id === "download") {
                        return desktopDownload ? (
                          <a
                            key={item.id}
                            href="https://fintheon.io/download"
                            className="fintheon-quick-settings-popover__item"
                            role="menuitem"
                            onClick={() => setShowQuickSettings(false)}
                          >
                            <Icon className="h-3.5 w-3.5 shrink-0" />
                            <span className="min-w-0 text-left">
                              <span className="fintheon-quick-settings-popover__label block text-[11px] font-semibold leading-tight">
                                {item.label}
                              </span>
                              <span className="fintheon-quick-settings-popover__description block truncate text-[10px] leading-tight">
                                {item.description}
                              </span>
                            </span>
                          </a>
                        ) : (
                          <span
                            key={item.id}
                            className="fintheon-quick-settings-popover__item opacity-55"
                            aria-disabled="true"
                          >
                            <Icon className="h-3.5 w-3.5 shrink-0" />
                            <span className="min-w-0 text-left">
                              <span className="fintheon-quick-settings-popover__label block text-[11px] font-semibold leading-tight">
                                {desktopDownloadLoading
                                  ? "Preparing download"
                                  : "Download unavailable"}
                              </span>
                              <span className="fintheon-quick-settings-popover__description block truncate text-[10px] leading-tight">
                                {item.description}
                              </span>
                            </span>
                          </span>
                        );
                      }
                      return (
                        <button
                          key={item.id}
                          type="button"
                          role="menuitem"
                          onClick={() => handleQuickSettingsAction(item.id)}
                          className="fintheon-quick-settings-popover__item"
                        >
                          <Icon className="h-3.5 w-3.5 shrink-0" />
                          <span className="min-w-0 text-left">
                            <span className="fintheon-quick-settings-popover__label block text-[11px] font-semibold leading-tight">
                              {item.label}
                            </span>
                            <span className="fintheon-quick-settings-popover__description block truncate text-[10px] leading-tight">
                              {item.description}
                            </span>
                          </span>
                        </button>
                      );
                    })}
                  </div>,
                  document.body,
                )}
            </div>
          )}
          {(compactLevel < 2 || topStepXEnabled) && (
            <div className={TOOLBAR_PILL_CLASS}>
              {showForumVoiceInPrimaryPill && <ProxVoiceHeaderControl />}
              {compactLevel < 1 && topStepXEnabled && (
                <FadingRuler orientation="vertical" className="mx-0.5" />
              )}
              {topStepXEnabled && (
                <button
                  onClick={() => {
                    if (totalBadgeCount > 0) {
                      setBulletinInitialSection("inbox");
                      setShowBulletin(true);
                      return;
                    }
                    toggleManualDnd();
                  }}
                  className={`relative toolbar-icon-btn ${dndActive ? "toolbar-active" : ""}`}
                  title={
                    totalBadgeCount > 0
                      ? "Open notification inbox"
                      : dndActive
                        ? "Do Not Disturb (ON)"
                        : "Notifications"
                  }
                >
                  {dndActive ? (
                    <BellOff className="w-3 h-3 toolbar-icon-active" />
                  ) : (
                    <Bell className="w-3 h-3" />
                  )}
                  {totalBadgeCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 inline-flex items-center justify-center min-w-[14px] h-[14px] px-0.5 rounded-full bg-red-500/80 text-white text-[8px] font-bold leading-none">
                      {totalBadgeCount > 99 ? "99+" : totalBadgeCount}
                    </span>
                  )}
                </button>
              )}
              {((compactLevel < 1 && topStepXEnabled) ||
                (compactLevel < 2 && topStepXEnabled)) &&
                compactLevel < 2 && (
                  <FadingRuler orientation="vertical" className="mx-0.5" />
                )}
              {compactLevel < 2 && (
                <button
                  onClick={handleQuickClock}
                  className={`toolbar-icon-btn ${quickClockPulse ? "toolbar-active" : ""}`}
                  title="Quick VolWatch observation"
                >
                  <Clock705
                    className={`w-3 h-3 ${quickClockPulse ? "toolbar-icon-active" : ""}`}
                  />
                </button>
              )}
              {compactLevel < 2 && (
                <FadingRuler orientation="vertical" className="mx-0.5" />
              )}
              {compactLevel < 2 && <HeaderLockButton />}
            </div>
          )}
          {compactLevel < 2 && voiceRoomWidget}
        </div>
      </div>

      <div
        className={`flex items-center gap-1.5 lg:gap-3 min-w-0 shrink-0 ${rightClusterClass}`}
      >
        <div className={rightInnerClass}>
          {psychAssistHeadingWidget}
          {activeTab === "performance" && performanceChatWidget}
          {showIframeControls &&
            (topStepXEnabled && onLayoutOptionChange ? (
              // iFrame active → Castra/Zen layout dropdown
              <div className="relative" ref={dropdownRef}>
                {/* [claude-code 2026-04-27] S46.4/F: chip chrome stripped per TP —
                  trigger sits flush with the toolbar like other icon buttons.
                  Resting state: no bg + no border. Hover keeps the accent flash. */}
                <button
                  onClick={() => setShowLayoutDropdown(!showLayoutDropdown)}
                  className="fintheon-header-menu-trigger px-2.5 h-7 rounded-[4px] text-xs font-medium text-[var(--fintheon-accent)] transition-colors flex items-center gap-1.5"
                  aria-label="Layout Options"
                  title="Layout Options"
                >
                  {
                    layoutOptions.find((opt) => opt.value === layoutOption)
                      ?.icon
                  }
                  {compactLevel < 1 && (
                    <span className="fintheon-zen-label">
                      {
                        layoutOptions.find((opt) => opt.value === layoutOption)
                          ?.label
                      }
                    </span>
                  )}
                  <ChevronDown
                    className={`w-3 h-3 transition-transform ${showLayoutDropdown ? "rotate-180" : ""}`}
                  />
                </button>
                {showLayoutDropdown &&
                  layoutDropdownPos &&
                  createPortal(
                    <div
                      ref={layoutPortalRef}
                      style={{
                        position: "fixed",
                        top: layoutDropdownPos.top,
                        left: layoutDropdownPos.left,
                        zIndex: 9999,
                      }}
                      className="fintheon-popover-surface w-72 overflow-hidden animate-dropdown-enter"
                    >
                      {layoutOptions.map((option) => (
                        <button
                          key={option.value}
                          onClick={() => {
                            onLayoutOptionChange(option.value);
                            setShowLayoutDropdown(false);
                          }}
                          className={`w-full border border-transparent px-4 py-3 text-left hover:bg-[var(--fintheon-accent)]/10 transition-colors flex items-start gap-3 ${
                            layoutOption === option.value
                              ? "border-[var(--fintheon-accent)]/28 text-[var(--fintheon-accent)]"
                              : ""
                          }`}
                        >
                          <div className="mt-0.5 text-[var(--fintheon-accent)]">
                            {option.icon}
                          </div>
                          <div className="flex-1">
                            <div className="text-sm font-medium text-[var(--fintheon-accent)] mb-1">
                              {option.label}
                            </div>
                            <div className="text-xs text-gray-400">
                              {option.description}
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>,
                    document.body,
                  )}
              </div>
            ) : (
              // iFrame off → platform/browser selection dropdown
              <div className="relative" ref={platformDropdownRef}>
                {/* [claude-code 2026-04-27] S46.4/F: chip chrome stripped per TP. */}
                <button
                  onClick={() => setShowPlatformDropdown(!showPlatformDropdown)}
                  className="fintheon-header-menu-trigger px-2.5 h-7 rounded-[4px] text-xs font-medium text-[var(--fintheon-accent)] transition-colors flex items-center gap-1.5"
                  aria-label="Select trading platform"
                  title="Select trading platform"
                >
                  <Monitor className="w-3 h-3" />
                  {compactLevel < 1 && (
                    <span className="fintheon-zen-label">
                      {selectedPlatformLabel}
                    </span>
                  )}
                  <ChevronDown
                    className={`w-3 h-3 transition-transform ${showPlatformDropdown ? "rotate-180" : ""}`}
                  />
                </button>
                {showPlatformDropdown &&
                  platformDropdownPos &&
                  createPortal(
                    <div
                      ref={platformPortalRef}
                      style={{
                        position: "fixed",
                        top: platformDropdownPos.top,
                        left: platformDropdownPos.left,
                        zIndex: 9999,
                      }}
                      className="fintheon-popover-surface w-72 overflow-hidden py-1 animate-dropdown-enter"
                    >
                      {platformOptions.map((option) => (
                        <button
                          key={option.value}
                          onClick={() => {
                            onPlatformSelect?.(option.value);
                            setShowPlatformDropdown(false);
                          }}
                          className={`w-full px-4 py-3 text-left transition-colors ${
                            selectedPlatform === option.value
                              ? "bg-[var(--fintheon-accent)]/15"
                              : "hover:bg-[var(--fintheon-accent)]/8"
                          }`}
                        >
                          <div
                            className={`text-xs font-semibold tracking-[0.14em] uppercase ${
                              selectedPlatform === option.value
                                ? "text-[var(--fintheon-accent)]"
                                : "text-gray-200"
                            }`}
                          >
                            {option.label}
                          </div>
                          <div
                            className={`text-[10px] mt-0.5 ${
                              selectedPlatform === option.value
                                ? "text-[var(--fintheon-accent)]/60"
                                : "text-gray-500"
                            }`}
                          >
                            {option.description}
                          </div>
                        </button>
                      ))}
                    </div>,
                    document.body,
                  )}
              </div>
            ))}
          {!isMobileSurface && (
            <div className="fintheon-toolbar-market-group flex flex-shrink-0 items-center gap-1.5">
              <TiltScoutHeaderFuse selectedSymbol={activeInstrument} />
              <HeaderVixChip data={ivData} compact={compactLevel >= 2} />
            </div>
          )}
          <div
            className={`${mobileTickerWrapperClass} fintheon-toolbar-iv-slot`}
          >
            {isMobileSurface ? (
              <>
                {econCountdownWidget ?? (
                  <IVScoreCard
                    data={ivData}
                    loading={ivLoading}
                    layoutOption={layoutOption}
                    compactCopy={!topStepXEnabled && compactLevel >= 1}
                    mobileCombined
                    latestReleasedEconEvent={latestReleasedEconEvent}
                  />
                )}
              </>
            ) : (
              <ToolbarDnD
                items={toolbarOrder}
                onOrderChange={setToolbarOrderState}
              >
                {(id) => {
                  if (id === "ivScore") {
                    return (
                      econCountdownWidget ?? (
                        <IVScoreCard
                          data={ivData}
                          loading={ivLoading}
                          layoutOption={layoutOption}
                          compactCopy={false}
                          latestReleasedEconEvent={latestReleasedEconEvent}
                        />
                      )
                    );
                  }
                  return null;
                }}
              </ToolbarDnD>
            )}
          </div>
          <div
            className={`fintheon-toolbar-actions ${TOOLBAR_PILL_CLASS} ${
              isMobileSurface
                ? "pointer-events-auto absolute right-2 top-1/2 -translate-y-1/2 mr-1.5"
                : ""
            }`}
          >
            {showIframeControls && onTopStepXDisable && (
              <button
                onClick={onTopStepXDisable}
                className={`toolbar-icon-btn ${topStepXEnabled ? "toolbar-active" : ""}`}
                title={
                  topStepXEnabled
                    ? "Power off trading browser"
                    : "Power on trading browser"
                }
                aria-label={
                  topStepXEnabled
                    ? "Power off trading browser"
                    : "Power on trading browser"
                }
              >
                <Power
                  className={`w-3 h-3 ${topStepXEnabled ? "toolbar-icon-active" : ""}`}
                  style={
                    topStepXEnabled
                      ? activeIconStyle("var(--fintheon-bullish)")
                      : undefined
                  }
                />
              </button>
            )}
            {showIframeControls && onTopStepXDisable && (
              <FadingRuler orientation="vertical" className="mx-0.5" />
            )}
            {showForumVoiceHandoff && (
              <ProxVoiceHeaderControl showWhenIdle={false} />
            )}
            {showForumVoiceHandoff && (
              <FadingRuler
                orientation="vertical"
                className="fintheon-toolbar-action-ruler mx-0.5"
              />
            )}
            <span className="header-bulletin-trigger-shell">
              <button
                ref={bulletinBtnRef}
                type="button"
                onClick={() => {
                  setBulletinInitialSection(undefined);
                  setShowBulletin(!showBulletin);
                }}
                className={`header-action-glass toolbar-icon-btn ${showBulletin ? "fintheon-black-liquid-glass toolbar-active" : ""}`}
                title="Bulletin"
              >
                <ClipboardList
                  className={`w-3 h-3 ${showBulletin ? "toolbar-icon-active" : ""}`}
                />
              </button>
              {bulletinDotTone !== "none" ? (
                <span
                  aria-hidden="true"
                  className={`header-bulletin-indicator ${
                    bulletinDotTone === "error"
                      ? "header-bulletin-indicator--error"
                      : ""
                  }`}
                />
              ) : null}
            </span>
            <StickyBulletin
              open={showBulletin}
              onClose={() => setShowBulletin(false)}
              anchorRef={bulletinBtnRef}
              variant={isMobileSurface ? "mobile-dropdown" : "desktop-popover"}
              initialSection={bulletinInitialSection}
            />
            {(!isMobileSurface || allowVoiceAssistant) && (
              <FadingRuler
                orientation="vertical"
                className="fintheon-toolbar-action-ruler mx-0.5"
              />
            )}
            {!isMobileSurface && onChatToggle && (
              <button
                onClick={onChatToggle}
                data-toolbar-fade="soft"
                className={`toolbar-icon-btn ${chatOpen ? "toolbar-active" : ""}`}
                title="Convene"
              >
                <MessageCircle
                  className={`w-3 h-3 ${chatOpen ? "toolbar-icon-active" : ""}`}
                />
              </button>
            )}
            {!isMobileSurface && onChatToggle && (
              <FadingRuler
                orientation="vertical"
                className="fintheon-toolbar-action-ruler mx-0.5"
              />
            )}
            {allowVoiceAssistant && (
              <span className="inline-flex" data-toolbar-fade="soft">
                <HeaderVoiceControl
                  compact={topStepXEnabled && layoutOption === "tickers-only"}
                />
              </span>
            )}
          </div>
          {shouldShowRightPanelToggle && (
            <PanelToggleButton side="right" label="right panel" />
          )}
        </div>
      </div>
    </div>
  );
}
